import { type ReactNode } from "react";

import clsx from "clsx";

import { Tooltip } from "@components/tooltip-container";
import { type MetricPrecision } from "@north-beam/nb-common/formatting";

import { getIsOnPace } from "../../shared/pacing-status";

type Props = {
  className?: string;
  actual: number;
  actualColor: string;
  actualText?: string;
  budgeted: number;
  budgetedText?: string;
  target: number;
  targetIconTooltipContent?: ReactNode;
  formatter: (value: number, precision?: MetricPrecision) => string;
  smallAnnotations?: boolean;
  dateRangeText: string;
};

// Sets alignment if text is left, right, or center based on the ratio of the value to the target
const alignmentCheck = (value: number, target: number) => {
  if (!value && !target) {
    return "right";
  } else if (!target || !value) {
    return "left";
  } else if (value / target > 0.85) {
    return "right";
  } else if (value / target < 0.15) {
    return "left";
  }
  return "center";
};

export const Slider = ({
  className,
  actual,
  actualColor,
  actualText = "ACTUAL",
  budgeted,
  budgetedText = "BUDGETED",
  target,
  targetIconTooltipContent,
  formatter,
  smallAnnotations = false,
  dateRangeText,
}: Props) => {
  let largestNumber = target;
  const isOverTarget = actual > target || budgeted > target;
  if (isOverTarget) {
    largestNumber = Math.max(actual, budgeted, target);
  }

  // Calculate the percentage of the largest number to use for the width of the bars
  // If the largest number is 0, set the percentage to 100 to show a full bar
  let actualPercentage = (actual / largestNumber) * 100;
  let budgetedPercentage = (budgeted / largestNumber) * 100;
  let targetPercentage = (target / largestNumber) * 100;

  if (!actualPercentage && !budgetedPercentage && !targetPercentage) {
    actualPercentage = budgetedPercentage = targetPercentage = 100;
  }

  const isOnPace = getIsOnPace(actual, target) && getIsOnPace(budgeted, target);

  return (
    <div className={clsx(className, "relative mx-3 flex-grow")}>
      <ActualAnnotation
        actualText={actualText}
        actualColor={actualColor}
        actualPercentage={actualPercentage}
        alignment={alignmentCheck(actual, largestNumber)}
        formatter={formatter}
        dateRangeText={dateRangeText}
        smallAnnotations={smallAnnotations}
        actual={actual}
      />
      <div className="flex items-center flex-col h-3.5 rounded-full bg-gray-2 overflow-hidden relative my-0.5">
        <ActualBar
          actualColor={actualColor}
          actualPercentage={actualPercentage}
        />
        <BudgetedBar budgetedPercentage={budgetedPercentage} />
      </div>
      {budgeted === undefined ? null : (
        <BudgetedAnnotation
          budgetedText={budgetedText}
          budgetedPercentage={budgetedPercentage}
          alignment={alignmentCheck(budgeted, largestNumber)}
          formatter={formatter}
          dateRangeText={dateRangeText}
          smallAnnotations={smallAnnotations}
          budgeted={budgeted}
        />
      )}
    </div>
  );
};

const ActualBar = ({
  actualColor,
  actualPercentage,
}: {
  actualColor: Props["actualColor"];
  actualPercentage: number;
}) => (
  <div
    className="h-1.5 absolute top-0 left-0 overflow-hidden"
    style={{ width: `${actualPercentage}%` }}
  >
    <div
      className="h-inherit w-full"
      style={{ backgroundColor: actualColor }}
    ></div>
  </div>
);

const ActualAnnotation = ({
  actualText,
  actualColor,
  actualPercentage,
  alignment,
  formatter,
  dateRangeText,
  actual,
  smallAnnotations,
}: {
  actualText: Props["actualText"];
  actualColor: Props["actualColor"];
  actualPercentage: number;
  alignment: "left" | "center" | "right";
  formatter: Props["formatter"];
  dateRangeText: Props["dateRangeText"];
  actual: Props["actual"];
  smallAnnotations: Props["smallAnnotations"];
}) => (
  <Tooltip
    content={
      <div className="font-semibold">
        <div className="text-xs tracking-wide mb-1">
          {`${actualText} TO DATE`}
        </div>
        <div className="text-gray+2 font-normal">{dateRangeText}</div>
        <div className="text-lg" style={{ color: actualColor }}>
          {formatter(actual, "exact")}
        </div>
      </div>
    }
    noInfoCircle
    className={clsx(
      "absolute top-0 -translate-y-full -translate-x-1/2 w-2",
      "flex flex-col",
      {
        "items-end": alignment === "right",
        "items-center": alignment === "center",
        "items-start": alignment === "left",
      },
    )}
    style={{ left: `${actualPercentage}%` }}
  >
    <div
      className={clsx("flex", {
        "flex-col items-center": !smallAnnotations,
        "items-center whitespace-nowrap space-x-1": smallAnnotations,
      })}
    >
      <div
        className={clsx("tracking-wide text-xs", {
          "font-semibold": smallAnnotations,
        })}
      >
        {actualText}
      </div>
      <div
        className={clsx(
          {
            "text-xl mb-1": !smallAnnotations,
            "text-xs my-1": smallAnnotations,
          },
          "font-semibol",
        )}
        style={{ color: actualColor }}
      >
        {formatter(actual)}
      </div>
    </div>
    <i
      className="fa-solid fa-triangle text-xs h-2 w-2 rotate-180"
      style={{ color: actualColor }}
    />
  </Tooltip>
);

const BudgetedBar = ({
  budgetedPercentage,
}: {
  budgetedPercentage: number;
}) => (
  <div
    className="h-1.5 absolute bottom-0 left-0 rotate-180 overflow-hidden"
    style={{ width: `${budgetedPercentage}%` }}
  >
    <div className="bg-cosmic h-inherit w-full"></div>
  </div>
);

const BudgetedAnnotation = ({
  budgetedText,
  budgetedPercentage,
  alignment,
  formatter,
  dateRangeText,
  budgeted,
  smallAnnotations,
}: {
  budgetedText: Props["budgetedText"];
  budgetedPercentage: number;
  alignment: "left" | "center" | "right";
  formatter: Props["formatter"];
  dateRangeText: Props["dateRangeText"];
  budgeted: Props["budgeted"];
  smallAnnotations: Props["smallAnnotations"];
}) => (
  <Tooltip
    content={
      <div className="font-semibold">
        <div className="text-xs tracking-wide mb-1">
          {`${budgetedText} TO DATE`}
        </div>
        <div className="text-gray+2 font-normal">{dateRangeText}</div>
        <div className="text-lg text-cosmic">
          {formatter(budgeted, "exact")}
        </div>
      </div>
    }
    noInfoCircle
    className={clsx(
      "absolute bottom-0 translate-y-full -translate-x-1/2 w-0",
      "flex flex-col-reverse",
      {
        "items-end": alignment === "right",
        "items-center": alignment === "center",
        "items-start": alignment === "left",
      },
    )}
    style={{ left: `${budgetedPercentage}%` }}
  >
    <div
      className={clsx("flex", {
        "flex-col-reverse items-center": !smallAnnotations,
        "items-center whitespace-nowrap space-x-1": smallAnnotations,
      })}
    >
      <div
        className={clsx(
          { "font-semibold": smallAnnotations },
          "tracking-wide text-xs",
        )}
      >
        {budgetedText}
      </div>
      <div
        className={clsx(
          {
            "text-xl mt-1": !smallAnnotations,
            "text-xs my-1": smallAnnotations,
          },
          "font-semibold text-cosmic",
        )}
      >
        {formatter(budgeted)}
      </div>
    </div>
    <i className="fa-solid fa-triangle text-xs h-2 w-2 text-cosmic" />
  </Tooltip>
);
